import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Package, 
  QrCode, 
  Eye, 
  Printer, 
  Building2, 
  User, 
  Phone,
  MapPin,
  Calendar,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import axiosInstance from '../../api/axiosInstance';
import QRCodeComponent from 'react-qr-code';
import GlobalLoader from "../../components/ui/GlobalLoader";

interface Order {
  _id: string;
  orderNumber: string;
  customerName: string;
  customerPhone: string;
  customerAddress?: string;
  pickupStatus: string;
  totalPrice?: number;
  deliveryDate?: string;
  createdAt: string;
  pharmacyId: string;
  pharmacyName: string;
  priority?: string;
  qrCode?: string;
  pharmacyPhone: string;
  pharmacyEmail: string;
  pharmacyAddress: string;
  location?: {
    address?: string;
    latitude?: number;
    longitude?: number;
  };
}

export function BarCodePage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [viewQRCodeDialogOpen, setViewQRCodeDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [generatingQRCodeFor, setGeneratingQRCodeFor] = useState<string | null>(null);
  const [regeneratingAll, setRegeneratingAll] = useState(false);

  useEffect(() => {
    fetchPharmacyOrders();
  }, []);

  const fetchPharmacyOrders = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/orders');
      const ordersData = response.data?.data?.orders || response.data?.data || response.data?.orders || response.data || [];
      
      console.log('Fetched pharmacy orders for QR codes:', ordersData.length);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error fetching pharmacy orders:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const generateQRCode = async (order: Order) => {
    try {
      setGeneratingQRCodeFor(order._id);
      
      // Generate QR code with full order data
      const qrCodeData = JSON.stringify({
        type: 'CRX_ORDER',
        orderNumber: order.orderNumber,
        _id: order._id,
        customerName: order.customerName,
        customerPhone: order.customerPhone,
        customerAddress: order.customerAddress,
        location: order.location,
        totalPrice: order.totalPrice,
        deliveryDate: order.deliveryDate,
        pickupStatus: order.pickupStatus,
        priority: order.priority,
        pharmacyId: order.pharmacyId,
        pharmacyName: order.pharmacyName,
        pharmacyPhone: order.pharmacyPhone,
        pharmacyEmail: order.pharmacyEmail,
        pharmacyAddress: order.pharmacyAddress,
        createdAt: order.createdAt
      });
      
      // Save QR code to database
      const response = await axiosInstance.patch(`/orders/${order._id}`, {
        qrCode: qrCodeData
      });
      
      if (response.data.success) {
        // Update local state
        setOrders(prev => prev.map(o => 
          o._id === order._id ? { ...o, qrCode: qrCodeData } : o
        ));
      } else {
        toast.error('Failed to save QR code');
      }
    } catch (error: any) {
      console.error('Error generating QR code:', error);
      toast.error(error.response?.data?.message || 'Failed to generate QR code');
    } finally {
      setGeneratingQRCodeFor(null);
    }
  };

  const handleViewQRCode = (order: Order) => {
    if (!order.qrCode) {
      toast.error('No QR code generated for this order');
      return;
    }
    setSelectedOrder(order);
    setViewQRCodeDialogOpen(true);
  };

  const handlePrintQRCode = async (order: Order) => {
    if (!order.qrCode) {
      toast.error('No QR code generated for this order');
      return;
    }
    
    try {
      // Use the QR code data
      let qrCodeData = order.qrCode;
      
      // If the stored QR code data doesn't look like JSON, regenerate it with full order data
      if (!qrCodeData || !qrCodeData.startsWith('{')) {
        console.log('Regenerating QR code data with full order information');
        qrCodeData = JSON.stringify({
          type: 'CRX_ORDER',
          orderNumber: order.orderNumber,
          _id: order._id,
          customerName: order.customerName,
          customerPhone: order.customerPhone,
          customerAddress: order.customerAddress,
          location: order.location,
          totalPrice: order.totalPrice,
          deliveryDate: order.deliveryDate,
          pickupStatus: order.pickupStatus,
          priority: order.priority,
          pharmacyId: order.pharmacyId,
          pharmacyName: order.pharmacyName,
          pharmacyPhone: order.pharmacyPhone,
          pharmacyEmail: order.pharmacyEmail,
          pharmacyAddress: order.pharmacyAddress,
          createdAt: order.createdAt
        });
      }
      
      // Generate QR code data URL using the qrcode library
      const QRCode = await import('qrcode');
      const qrCodeDataUrl = await QRCode.toDataURL(qrCodeData, {
        width: 512, // Increased for more data
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      // Create a temporary window for printing QR code
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow popups to print QR code');
        return;
      }
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Print QR Code - ${order.orderNumber}</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body {
                font-family: Arial, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                margin: 0;
                padding: 20px;
                background: white;
              }
              .qrcode-container {
                text-align: center;
                border: 2px solid #000;
                padding: 30px;
                background: white;
                max-width: 500px;
                width: 100%;
              }
              .qrcode-header {
                margin-bottom: 20px;
              }
              .qrcode-header h2 {
                margin: 5px 0;
                font-size: 20px;
              }
              .qrcode-footer {
                margin-top: 20px;
                padding-top: 15px;
                border-top: 1px solid #ccc;
              }
              .qrcode-footer p {
                margin: 5px 0;
                font-size: 14px;
                color: #333;
              }
              .qrcode-image {
                margin: 20px auto;
                border: 1px solid #ccc;
                display: block;
                width: 350px;
                height: 350px;
                object-fit: contain;
              }
              @media print {
                @page {
                  margin: 0.5in;
                  size: A4;
                }
                body {
                  background: white !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  height: auto !important;
                  display: block !important;
                }
                .qrcode-container {
                  border: none !important;
                  padding: 0 !important;
                  margin: 0 auto !important;
                  max-width: 100% !important;
                  page-break-inside: avoid;
                }
                .qrcode-header h2 {
                  font-size: 24px !important;
                }
                .qrcode-image {
                  width: 400px !important;
                  height: 400px !important;
                  margin: 20px auto !important;
                }
              }
            </style>
          </head>
          <body>
            <div class="qrcode-container">
              <div class="qrcode-header">
                <h2>🏥 ${order.pharmacyName || 'Pharmacy'}</h2>
              </div>
              <img class="qrcode-image" src="${qrCodeDataUrl}" alt="QR Code" />
              <div class="qrcode-footer">
                <p><strong>Website:</strong> https://courier-rx.com</p>
                <p><strong>Support:</strong>+1 862 295 8034</p>
              </div>
            </div>
            <script>
              // Auto print after a short delay
              setTimeout(() => {
                window.print();
              }, 500);
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      
    } catch (error) {
      console.error('Error generating QR code for print:', error);
      toast.error('Failed to generate QR code for printing');
    }
  };

  const handlePrintQRFromDialog = () => {
    if (!selectedOrder || !selectedOrder.qrCode) return;
    handlePrintQRCode(selectedOrder);
  };

  const regenerateAllQRCodes = async () => {
    try {
      setRegeneratingAll(true);
      
      // Find orders without JSON QR codes
      const ordersToUpdate = orders.filter(order => 
        !order.qrCode || !order.qrCode.startsWith('{')
      );
      
      if (ordersToUpdate.length === 0) {
        toast.success('All QR codes are already up to date!');
        return;
      }
      
      toast.info(`Regenerating ${ordersToUpdate.length} QR codes...`);
      
      // Update each order with full order data QR code
      for (const order of ordersToUpdate) {
        try {
          const qrCodeData = JSON.stringify({
            type: 'CRX_ORDER',
            orderNumber: order.orderNumber,
            _id: order._id,
            customerName: order.customerName,
            customerPhone: order.customerPhone,
            customerAddress: order.customerAddress,
            location: order.location,
            totalPrice: order.totalPrice,
            deliveryDate: order.deliveryDate,
            pickupStatus: order.pickupStatus,
            priority: order.priority,
            pharmacyId: order.pharmacyId,
            pharmacyName: order.pharmacyName,
            pharmacyPhone: order.pharmacyPhone,
            pharmacyEmail: order.pharmacyEmail,
            pharmacyAddress: order.pharmacyAddress,
            createdAt: order.createdAt
          });
          
          await axiosInstance.patch(`/orders/${order._id}`, {
            qrCode: qrCodeData
          });
          
          // Update local state
          setOrders(prev => prev.map(o => 
            o._id === order._id ? { ...o, qrCode: qrCodeData } : o
          ));
        } catch (error) {
          console.error(`Failed to update QR code for order ${order.orderNumber}:`, error);
        }
      }
      
      toast.success(`Successfully regenerated ${ordersToUpdate.length} QR codes!`);
    } catch (error) {
      console.error('Error regenerating QR codes:', error);
      toast.error('Failed to regenerate QR codes');
    } finally {
      setRegeneratingAll(false);
    }
  };

  // Get priority badge color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'same-day':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'next-day':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Filter orders based on search and priority
  const filteredOrders = orders.filter(order => {
    // Search filter
    const searchMatch = !searchTerm || (
      order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerPhone?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Priority filter
    const priorityMatch = priorityFilter === 'all' || order.priority === priorityFilter;

    return searchMatch && priorityMatch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <GlobalLoader isLoading={true} message="Processing please wait..." subMessage="Please wait" />
          <p className="mt-2 text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">QR Codes</h1>
          <p className="text-gray-600">Manage QR codes for your pharmacy orders</p>
        </div>
        <div className="flex gap-2">
          {/* <Button 
            onClick={regenerateAllQRCodes}
            disabled={regeneratingAll}
            variant="outline"
            className="text-purple-600 hover:text-purple-700"
          >
            {regeneratingAll ? (
              <>
                <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin mr-2" />
                Regenerating...
              </>
            ) : (
              <>
                <QrCode className="w-4 h-4 mr-2" />
                Regenerate All QR Codes
              </>
            )}
          </Button> */}
          {/* <Badge variant="outline" className="text-lg px-4 py-2">
            {filteredOrders.length} Orders
          </Badge> */}
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by order number, customer name, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11"
              />
            </div>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Filter by Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="same-day">Same Day</SelectItem>
                <SelectItem value="next-day">Next Day</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders List */}
      <Card>
        <CardHeader>
          <CardTitle>All Orders ({filteredOrders.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
              <p className="text-gray-500">Your pharmacy orders will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map((order) => (
                <div key={order._id} className="border rounded-lg p-5 bg-white">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4 pb-4 border-b">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {order.orderNumber}
                        </h3>
                        <Badge className="bg-yellow-100 text-yellow-800">
                          {order.pickupStatus}
                        </Badge>
                        {order.priority && (
                          <Badge variant="outline" className={getPriorityColor(order.priority)}>
                            {order.priority === 'same-day' ? 'Same Day' : 'Next Day'}
                          </Badge>
                        )}
                        {order.qrCode && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700">
                            <QrCode className="w-3 h-3 mr-1" />
                            QR Code
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* QR Code Buttons */}
                      {!order.qrCode ? (
                        <Button 
                          onClick={() => generateQRCode(order)}
                          disabled={generatingQRCodeFor === order._id}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          size="sm"
                        >
                          {generatingQRCodeFor === order._id ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              Generating...
                            </>
                          ) : (
                            <>
                              <QrCode className="w-4 h-4 mr-2" />
                              Generate QR Code
                            </>
                          )}
                        </Button>
                      ) : (
                        <>
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewQRCode(order)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View QR Code
                          </Button>
                          <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => handlePrintQRCode(order)}
                            className="text-purple-600 hover:text-purple-700"
                          >
                            <Printer className="w-4 h-4 mr-1" />
                            Print QR Code
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Order Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Pharmacy Info */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-gray-700">Pharmacy</span>
                      </div>
                      <p className="font-medium text-gray-900">{order.pharmacyName || 'N/A'}</p>
                    </div>

                    {/* Customer Info */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-700">Customer</span>
                      </div>
                      <p className="font-medium text-gray-900">{order.customerName || 'N/A'}</p>
                      {order.customerPhone && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Phone className="w-3 h-3" />
                          <span>{order.customerPhone}</span>
                        </div>
                      )}
                    </div>

                    {/* Location Info */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-gray-700">Location</span>
                      </div>
                      <p className="text-sm text-gray-700 line-clamp-2">
                        {order.location?.address || order.customerAddress || 'N/A'}
                      </p>
                    </div>

                    {/* Date Info */}
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Calendar className="w-4 h-4 text-orange-600" />
                        <span className="text-sm font-medium text-gray-700">Created</span>
                      </div>
                      <p className="text-sm text-gray-700">
                        {new Date(order.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View QR Code Dialog */}
      <Dialog open={viewQRCodeDialogOpen} onOpenChange={setViewQRCodeDialogOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl">Order QR Code</DialogTitle>
            <DialogDescription>
              QR Code for order {selectedOrder?.orderNumber}
            </DialogDescription>
          </DialogHeader>

          {selectedOrder && selectedOrder.qrCode && (
            <div className="space-y-4">
              {/* QR Code Display */}
              <div 
                className="bg-white border-2 border-gray-300 rounded-lg p-8 text-center"
              >
                <div className="mb-6">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    <h2 className="text-xl font-bold text-gray-900">{selectedOrder.pharmacyName}</h2>
                  </div>
                </div>
                
                <div className="flex justify-center">
                  {selectedOrder.qrCode && selectedOrder.qrCode.length <= 100 && !selectedOrder.qrCode.startsWith('data:image') ? (
                    <QRCodeComponent 
                      value={selectedOrder.qrCode} 
                      size={256}
                      level="H"
                      style={{ height: "auto", maxWidth: "100%", width: "256px" }}
                    />
                  ) : selectedOrder.qrCode && selectedOrder.qrCode.startsWith('data:image') ? (
                    <img 
                      src={selectedOrder.qrCode} 
                      alt="QR Code" 
                      className="w-64 h-64 border border-gray-300 rounded-lg"
                    />
                  ) : (
                    <div className="w-64 h-64 flex items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg">
                      <div className="text-center p-4">
                        <QrCode className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-gray-600 mb-2">QR Code needs regeneration</p>
                        <p className="text-sm text-gray-500">Click "Generate QR Code" to create a new one</p>
                        <Button 
                          onClick={() => {
                            generateQRCode(selectedOrder);
                            setViewQRCodeDialogOpen(false);
                          }}
                          className="mt-3 bg-purple-600 hover:bg-purple-700 text-white"
                          size="sm"
                        >
                          <QrCode className="w-4 h-4 mr-2" />
                          Generate New QR Code
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Website and Support Info */}
              <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
                <p className="font-medium">https://courier-rx.com</p>
                <p className="text-sm text-gray-600">Support:+1 862 295 8034</p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setViewQRCodeDialogOpen(false)}>
              Close
            </Button>
            <Button 
              onClick={handlePrintQRFromDialog}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print QR Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
